// Action handlers for suggested actions

import { CUSTOM_USE_CASE_ROUTES } from '../constants';
import { buildApiUrl } from '../../../services/apiConfig';

export const createActionHandler = (
  action,
  {
    setInput,
    handleSendMessage,
    navigate,
    setConversationId,
    setClarificationType,
    setOriginalPrompt,
    setMessages,
    fileUploaded,
    collectionName,
    messages,
    conversationHistory,
    setPendingEnhance,
  }
) => {
  const executeWithDelay = (inputText) => {
    setInput(inputText);
    setTimeout(() => handleSendMessage(), 100);
  };

  const handlers = {
    skip_question: () => executeWithDelay("Skip this question"),
    
    select_domain: () => executeWithDelay(action.value),
    
    select_visualization: () => executeWithDelay(action.value),
    
    select_data_source: () => executeWithDelay(action.value),
    
    select_purpose: () => executeWithDelay(action.value),
    
    start_over: () => {
      setConversationId(null);
      setClarificationType(null);
      setOriginalPrompt("");
      setMessages(prev => [
        ...prev,
        { text: "Let's start over. How can I help you today?", sender: "bot" }
      ]);
    },
    
    provide_details: () => {
      setInput("");
      document.querySelector('input[type="text"]')?.focus();
    },
    
    suggest_create: () => {
      // Backend will extract domain context from conversation history
      executeWithDelay("Create a new use case");
    },
    
    create_use_case: () => {
      // Backend will extract domain context from conversation history
      executeWithDelay("Create a new use case");
    },
    
    open_use_case: () => {
      const customerParam = action.customer_id ? `?customerId=${action.customer_id}` : '';

      // First check if this use case has a custom route
      const customRoute = CUSTOM_USE_CASE_ROUTES[action.use_case_name];
      if (customRoute) {
        sessionStorage.setItem('chatbot_clear_history', '1');
        if (customRoute.startsWith('http')) {
          window.location.href = customRoute;
        } else {
          navigate(`${customRoute}${customerParam}`);
        }
        return;
      }

      // Otherwise use slug-based route
      sessionStorage.setItem('chatbot_clear_history', '1');
      if (action.use_case_slug) {
        navigate(`/use-case/${action.use_case_slug}${customerParam}`);
      } else if (action.use_case_id) {
        navigate(`/use-case/${action.use_case_id}${customerParam}`);
      } else {
        sessionStorage.removeItem('chatbot_clear_history');
        executeWithDelay(`Open ${action.use_case_name || 'the use case'}`);
      }
    },

    model_on_use_case: () => {
      sessionStorage.setItem('chatbot_clear_history', '1');
      if (action.use_case_slug) {
        // Navigate using slug for clean URLs
        navigate(`/use-case/${action.use_case_slug}`);
      } else if (action.use_case_id) {
        // Fallback to ID if slug not available
        navigate(`/use-case/${action.use_case_id}`);
      } else {
        sessionStorage.removeItem('chatbot_clear_history');
        executeWithDelay(`I want to model a use case based on ${action.use_case_name || 'an existing one'}`);
      }
    },
    
    browse_use_cases: () => navigate("/marketplace"),
    
    upload_documents: () => document.getElementById("file-upload")?.click(),
    
    use_agent: () => {
      if (action.agent_id) navigate(`/agent/${action.agent_id}`);
    },
    
    create_visualization: () => executeWithDelay("Create a visualization of my data"),
    
    configure_agent: () => executeWithDelay("Help me configure an agent"),
    
    create_dashboard: () => {
      const dashboardType = action.dashboard_type || "";
      const domain = action.domain ? ` for ${action.domain}` : "";
      executeWithDelay(`Create a ${dashboardType} dashboard${domain}`);
    },
    
    analyze_documents: () => {
      if (fileUploaded && collectionName) {
        const query = action.query || "provide insights";
        executeWithDelay(`Analyze the documents I uploaded and ${query}`);
      } else {
        document.getElementById("file-upload")?.click();
      }
    },
    
    create_data_tables: async () => {
      const lastBotMsg = [...messages].reverse().find(m => m.sender === 'bot');
      const rawHtml = lastBotMsg?.text || '';
      const description = rawHtml.replace(/<[^>]+>/g, '').trim();

      // Search all bot messages for the use case domain: text between "in " and the first ","
      let category = null;
      for (const msg of messages) {
        if (msg.sender !== 'bot') continue;
        const plain = (msg.text || '').replace(/<[^>]+>/g, '').replace(/\*\*/g, '');
        const m = plain.match(/\bin\s+([^,\n]{3,60}),/);
        if (m) { category = m[1].trim(); break; }
      }

      if (!description) {
        setMessages(prev => [...prev, { text: 'No architecture found to generate tables from.', sender: 'bot' }]);
        return;
      }

      setMessages(prev => [...prev, { text: 'Checking A3GoldDemo for relevant existing data...', sender: 'bot' }]);

      try {
        const checkResp = await fetch(`${buildApiUrl('EXPORT_USE_CASE')}/chat-check-tables`, {
          method: 'POST',
          headers: { 'Content-Type': 'text/plain' },
          body: description,
        });
        const checkData = await checkResp.json();

        if (checkData.relevant_tables?.length > 0) {
          const tableList = checkData.relevant_tables
            .map(t => `<li style="margin-bottom:6px;"><strong>${t.name}</strong><br/><span style="font-size:11px;color:#6b7280;">${(t.columns || []).slice(0, 8).join(', ')}${(t.columns || []).length > 8 ? '…' : ''}</span><br/><span style="font-size:11px;color:#374151;">${t.relevance || ''}</span></li>`)
            .join('');
          const recommendation = checkData.recommendation || '';

          const buildTableAccordion = (tbl, isFirst) => {
            if (!tbl.rows || tbl.rows.length === 0) return '';
            const headerCells = tbl.columns.map(c =>
              `<th style="border:1px solid #d1d5db;padding:4px 8px;background:#f3f4f6;white-space:nowrap;font-size:11px;position:sticky;top:0;">${c}</th>`
            ).join('');
            const bodyRows = tbl.rows.map(row =>
              `<tr>${tbl.columns.map(c => `<td style="border:1px solid #d1d5db;padding:4px 8px;white-space:nowrap;font-size:11px;">${row[c] ?? ''}</td>`).join('')}</tr>`
            ).join('');
            return `<details${isFirst ? ' open' : ''} style="margin-bottom:6px;border:1px solid #e5e7eb;border-radius:6px;overflow:hidden;">
<summary style="padding:6px 10px;background:#f9fafb;cursor:pointer;font-size:12px;font-weight:600;list-style:none;display:flex;justify-content:space-between;">
  <span>${tbl.name}</span><span style="color:#6b7280;font-weight:400;">${tbl.rows.length} rows · ${tbl.columns.length} cols</span>
</summary>
<div style="overflow-x:auto;overflow-y:auto;max-height:240px;">
  <table style="border-collapse:collapse;width:100%;"><thead><tr>${headerCells}</tr></thead><tbody>${bodyRows}</tbody></table>
</div>
</details>`;
          };

          const tableData = checkData.table_data || [];
          const accordionHtml = tableData.length > 0
            ? `<br/><strong style="font-size:12px;color:#374151;">Preview existing data:</strong><br/>${tableData.map((t, i) => buildTableAccordion(t, i === 0)).join('')}`
            : '';

          setMessages(prev => {
            const updated = [...prev];
            updated[updated.length - 1] = {
              text: `Found <strong>${checkData.relevant_tables.length}</strong> relevant table(s) in <strong>A3GoldDemo</strong>:<br/><ul style="margin:8px 0 8px 16px;">${tableList}</ul><em>${recommendation}</em>${accordionHtml}`,
              sender: 'bot',
              suggested_actions: [
                    { text: 'Use existing data', action: 'use_existing_data', tables: tableData, relevant_tables: checkData.relevant_tables, category },
                    { text: 'Create new synthetic data', action: 'create_data_tables_confirmed', description, category },
                    { text: 'Upload your own file', action: 'upload_for_sdv', category },
                    { text: 'Use a different database', action: 'select_different_db', description, category },
                    { text: 'Cancel', action: 'start_over' },
                  ],
            };
            return updated;
          });
          return;
        }
      } catch (_) {
        // DB check failed — proceed to generation anyway
      }

      setMessages(prev => {
        const updated = [...prev];
        updated[updated.length - 1] = { text: 'Generating schema from the architecture...', sender: 'bot' };
        return updated;
      });

      try {
        const resp = await fetch(`${buildApiUrl('EXPORT_USE_CASE')}/chat-schema`, {
          method: 'POST',
          headers: { 'Content-Type': 'text/plain' },
          body: description,
        });
        const data = await resp.json();

        if (!data.success) {
          throw new Error(data.detail || 'Schema generation failed');
        }

        const tableRows = (data.tables || [])
          .map(t => {
            const colNames = t.columns.map(c => c.name || c).join(', ');
            return `<li style="margin-bottom:4px;"><strong>${t.name}</strong><br/><span style="font-size:11px;color:#6b7280;">${colNames}</span></li>`;
          })
          .join('');

        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = {
            text: `Schema ready — <strong>${data.table_count}</strong> table(s) proposed:<br/><ul style="margin:8px 0 8px 16px;">${tableRows}</ul>Click <strong>Generate data</strong> to create the full dataset.`,
            sender: 'bot',
            suggested_actions: [
              { text: 'Generate data', action: 'chat_generate', description, category },
              { text: 'Upload your own file', action: 'upload_for_sdv', category },
              { text: 'Use a different database', action: 'select_different_db', description, category },
              { text: 'Cancel', action: 'start_over' },
            ],
          };
          return updated;
        });
      } catch (e) {
        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = { text: `Failed to generate schema: ${e.message}`, sender: 'bot' };
          return updated;
        });
      }
    },

    create_data_tables_confirmed: async () => {
      const lastBotMsg = [...messages].reverse().find(m => m.sender === 'bot');
      const description = action.description ||
        (lastBotMsg?.text || '').replace(/<[^>]+>/g, '').trim();

      setMessages(prev => [...prev, { text: 'Generating schema from the architecture...', sender: 'bot' }]);

      try {
        const resp = await fetch(`${buildApiUrl('EXPORT_USE_CASE')}/chat-schema`, {
          method: 'POST',
          headers: { 'Content-Type': 'text/plain' },
          body: description,
        });
        const data = await resp.json();

        if (!data.success) throw new Error(data.detail || 'Schema generation failed');

        const tableRows = (data.tables || [])
          .map(t => {
            const colNames = t.columns.map(c => c.name || c).join(', ');
            return `<li style="margin-bottom:4px;"><strong>${t.name}</strong><br/><span style="font-size:11px;color:#6b7280;">${colNames}</span></li>`;
          })
          .join('');

        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = {
            text: `Schema ready — <strong>${data.table_count}</strong> table(s) proposed:<br/><ul style="margin:8px 0 8px 16px;">${tableRows}</ul>Click <strong>Generate data</strong> to create the full dataset.`,
            sender: 'bot',
            suggested_actions: [
              { text: 'Generate data', action: 'chat_generate', description, category: action.category || null },
              { text: 'Use a different database', action: 'select_different_db', description, category: action.category || null },
              { text: 'Cancel', action: 'start_over' },
            ],
          };
          return updated;
        });
      } catch (e) {
        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = { text: `Failed to generate schema: ${e.message}`, sender: 'bot' };
          return updated;
        });
      }
    },

    chat_generate: async () => {
      setMessages(prev => [...prev, { text: 'Generating synthetic data...', sender: 'bot' }]);

      try {
        const resp = await fetch(`${buildApiUrl('EXPORT_USE_CASE')}/chat-generate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
        });
        const data = await resp.json();

        if (!data.success) {
          throw new Error(data.detail || 'Data generation failed');
        }

        const buildPreviewTable = (t) => {
          if (!t.preview || t.preview.length === 0) return '';
          const cols = t.columns;
          const headerCells = cols.map(c => `<th style="border:1px solid #d1d5db;padding:4px 8px;background:#f3f4f6;white-space:nowrap;">${c}</th>`).join('');
          const bodyRows = t.preview.map(row =>
            `<tr>${cols.map(c => `<td style="border:1px solid #d1d5db;padding:4px 8px;white-space:nowrap;">${row[c] ?? ''}</td>`).join('')}</tr>`
          ).join('');
          return `<div style="overflow-x:auto;margin:6px 0 12px;">
            <table style="border-collapse:collapse;font-size:11px;">
              <thead><tr>${headerCells}</tr></thead>
              <tbody>${bodyRows}</tbody>
            </table>
          </div>`;
        };

        const tablesHtml = (data.tables || []).map(t =>
          `<div style="margin-bottom:4px;"><strong>${t.name}</strong> — ${t.total_rows} rows</div>${buildPreviewTable(t)}`
        ).join('');

        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = {
            text: `Data ready — <strong>${data.table_count}</strong> table(s) generated:<br/><br/>${tablesHtml}<br/>Accept to save as Excel, or reject to discard.`,
            sender: 'bot',
            suggested_actions: [
              { text: 'Accept & save', action: 'chat_confirm_accept', description: action.description, category: action.category },
              { text: 'Reject', action: 'chat_confirm_reject' },
            ],
          };
          return updated;
        });
      } catch (e) {
        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = { text: `Failed to generate data: ${e.message}`, sender: 'bot' };
          return updated;
        });
      }
    },

    chat_confirm_accept: async () => {
      try {
        // Derive a base name (extension corrected after fetch)
        const baseDisplayName = action.category
          ? action.category
          : (() => {
              const rawDesc = (action.description || '').replace(/<[^>]+>/g, '').trim();
              const firstLine = rawDesc.split('\n').map(l => l.replace(/^#+\s*/, '').trim()).find(l => l.length > 5 && l.length < 80);
              return (firstLine || 'synthetic_data').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '').slice(0, 50);
            })();

        const resp = await fetch(`${buildApiUrl('EXPORT_USE_CASE')}/chat-confirm`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'accept' }),
        });
        const data = await resp.json();
        const actualExt = data.file && data.file.includes('.') ? data.file.slice(data.file.lastIndexOf('.')) : '.xlsx';
        const displayFilename = `${baseDisplayName}${actualExt}`;
        setMessages(prev => [...prev, {
          text: data.success
            ? `Saved — ${data.total_rows} rows across ${data.sheets?.length} sheet(s).`
            : `Save failed: ${data.detail}`,
          sender: 'bot',
          ...(data.success && {
            suggested_actions: [
              { text: `Download ${displayFilename}`, action: 'do_file_download', filename: data.file, displayName: displayFilename },
              { text: 'Enhance & refine data', action: 'enhance_data', filename: data.file, category: action.category },
              { text: 'Get SQL for database', action: 'get_sql', category: action.category },
              { text: 'Done', action: 'start_over' },
            ],
          }),
        }]);
      } catch (e) {
        setMessages(prev => [...prev, { text: `Error saving: ${e.message}`, sender: 'bot' }]);
      }
    },

    open_synthiq: () => {
      window.open('http://54.204.131.48:3003/', '_blank');
    },

    enhance_data: async () => {
      const filename = action.filename;
      if (!filename) {
        setMessages(prev => [...prev, { text: 'No generated file found to enhance.', sender: 'bot' }]);
        return;
      }

      setMessages(prev => [...prev, {
        text: `Uploading <strong>${filename}</strong> to SDV for enhancement — this may take a minute...`,
        sender: 'bot',
      }]);

      try {
        const resp = await fetch(`${buildApiUrl('EXPORT_USE_CASE')}/chat-enhance-start`, {
          method: 'POST',
          headers: { 'Content-Type': 'text/plain' },
          body: filename,
        });
        const data = await resp.json();

        if (!data.success) throw new Error('Enhancement start failed');

        const outputFile = data.generation?.outputFiles?.[0];
        const sheets = data.sheets_preview || [];
        const totalRows = sheets.reduce((s, sh) => s + sh.total_rows, 0) || outputFile?.rows || '?';

        const buildSheetHtml = (sheet, isFirst) => {
          const headerCells = sheet.columns.map(c =>
            `<th style="border:1px solid #d1d5db;padding:4px 8px;background:#f3f4f6;white-space:nowrap;font-size:11px;position:sticky;top:0;">${c}</th>`
          ).join('');
          const bodyRows = sheet.rows.map(row =>
            `<tr>${sheet.columns.map(c => `<td style="border:1px solid #d1d5db;padding:4px 8px;white-space:nowrap;font-size:11px;">${row[c] ?? ''}</td>`).join('')}</tr>`
          ).join('');
          return `<details${isFirst ? ' open' : ''} style="margin-bottom:6px;border:1px solid #e5e7eb;border-radius:6px;overflow:hidden;">
  <summary style="padding:6px 10px;background:#f9fafb;cursor:pointer;font-size:12px;font-weight:600;list-style:none;display:flex;justify-content:space-between;">
    <span>${sheet.name}</span><span style="color:#6b7280;font-weight:400;">${sheet.total_rows} rows · ${sheet.columns.length} cols</span>
  </summary>
  <div style="overflow-x:auto;overflow-y:auto;max-height:260px;">
    <table style="border-collapse:collapse;width:100%;">
      <thead><tr>${headerCells}</tr></thead>
      <tbody>${bodyRows}</tbody>
    </table>
  </div>
</details>`;
        };

        const sheetsHtml = sheets.length > 0
          ? sheets.map((sh, i) => buildSheetHtml(sh, i === 0)).join('')
          : '';

        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = {
            text: `SDV enhancement complete — <strong>${totalRows} rows</strong> generated across <strong>${sheets.length || 1}</strong> sheet(s).<br/>${sheetsHtml}<br/>Accept the data or tell me what to change.`,
            sender: 'bot',
            suggested_actions: [
              { text: 'Accept & download', action: 'enhance_direct_download', filename: outputFile?.downloadId || outputFile?.name, category: action.category },
              { text: 'Change something', action: 'enhance_prompt_regenerate', available_columns: data.available_columns || [], table_columns_map: Object.fromEntries(sheets.map(sh => [sh.name, sh.columns])), file_id: outputFile?.downloadId || outputFile?.name, category: action.category },
              { text: 'Cancel', action: 'start_over' },
            ],
          };
          return updated;
        });
      } catch (e) {
        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = { text: `Enhancement failed: ${e.message}`, sender: 'bot' };
          return updated;
        });
      }
    },

    enhance_prompt_regenerate: () => {
      const availableCols = action.available_columns || [];
      const tableColumnsMap = action.table_columns_map || {};
      const fileId = action.file_id || null;
      const colList = availableCols.slice(0, 15).join(', ');
      const extra = availableCols.length > 15 ? ` (+${availableCols.length - 15} more)` : '';
      setMessages(prev => [...prev, {
        text: `What would you like to change? Type your instruction below.<br/><br/><em style="font-size:12px;color:#4b5563;">Available columns: ${colList}${extra}</em><br/><span style="font-size:11px;color:#9ca3af;">e.g. "Make salary between 50000 and 100000" or "Set age to random values between 25 and 65"</span>`,
        sender: 'bot',
      }]);
      setPendingEnhance({ pendingAction: 'enhance_do_regenerate', available_columns: availableCols, table_columns_map: tableColumnsMap, file_id: fileId, category: action.category });
    },

    enhance_do_regenerate: async () => {
      const instruction = action.instruction;
      const availableCols = action.available_columns || [];
      const tableColumnsMap = action.table_columns_map || {};
      const fileId = action.file_id || null;

      setMessages(prev => [...prev, {
        text: `Applying: "<em>${instruction}</em>"...`,
        sender: 'bot',
      }]);

      try {
        const resp = await fetch(`${buildApiUrl('EXPORT_USE_CASE')}/chat-enhance-regenerate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ instruction, available_columns: availableCols, table_columns_map: tableColumnsMap, ...(fileId && { file_id: fileId }) }),
        });
        const data = await resp.json();

        if (!data.success) throw new Error(data.detail || 'Regeneration failed');

        const cols = data.columns_updated || [];
        const sheets = data.sheets_preview || [];
        const totalRows = sheets.reduce((s, sh) => s + sh.total_rows, 0) || data.total_rows || '?';

        const buildSheetHtml = (sheet, isFirst) => {
          const updatedSet = new Set(cols);
          const headerCells = sheet.columns.map(c =>
            `<th style="border:1px solid #d1d5db;padding:4px 8px;background:${updatedSet.has(c) ? '#fef9c3' : '#f3f4f6'};white-space:nowrap;font-size:11px;position:sticky;top:0;">${c}${updatedSet.has(c) ? ' ✱' : ''}</th>`
          ).join('');
          const bodyRows = sheet.rows.map(row =>
            `<tr>${sheet.columns.map(c => `<td style="border:1px solid #d1d5db;padding:4px 8px;white-space:nowrap;font-size:11px;${updatedSet.has(c) ? 'background:#fefce8;' : ''}">${row[c] ?? ''}</td>`).join('')}</tr>`
          ).join('');
          return `<details${isFirst ? ' open' : ''} style="margin-bottom:6px;border:1px solid #e5e7eb;border-radius:6px;overflow:hidden;">
  <summary style="padding:6px 10px;background:#f9fafb;cursor:pointer;font-size:12px;font-weight:600;list-style:none;display:flex;justify-content:space-between;">
    <span>${sheet.name}</span><span style="color:#6b7280;font-weight:400;">${sheet.total_rows} rows · ${sheet.columns.length} cols</span>
  </summary>
  <div style="overflow-x:auto;overflow-y:auto;max-height:260px;">
    <table style="border-collapse:collapse;width:100%;">
      <thead><tr>${headerCells}</tr></thead>
      <tbody>${bodyRows}</tbody>
    </table>
  </div>
</details>`;
        };

        const sheetsHtml = sheets.length > 0
          ? sheets.map((sh, i) => buildSheetHtml(sh, i === 0)).join('')
          : '';

        const changedNote = cols.length > 0
          ? `<p style="font-size:11px;color:#92400e;margin:4px 0 6px;">✱ Highlighted columns show pending changes — not saved yet.</p>`
          : '';

        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = {
            text: `Updated <strong>${cols.length}</strong> column(s): <em>${cols.join(', ')}</em> — ${totalRows} rows total.<br/>${changedNote}${sheetsHtml}<br/>Accept changes or keep adjusting.`,
            sender: 'bot',
            suggested_actions: [
              { text: 'Accept & download', action: 'enhance_confirm_accept', filename: data.file, category: action.category },
              { text: 'Accept & change something', action: 'enhance_accept_and_change', filename: data.file, columns_updated: cols, sheets_preview: sheets, available_columns: availableCols, table_columns_map: tableColumnsMap, category: action.category },
              { text: 'Reject changes', action: 'enhance_confirm_reject' },
            ],
          };
          return updated;
        });
      } catch (e) {
        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = { text: `Regeneration failed: ${e.message}`, sender: 'bot' };
          return updated;
        });
      }
    },

    enhance_direct_download: () => {
      const filename = action.filename;
      if (!filename) {
        setMessages(prev => [...prev, { text: 'No file available to download.', sender: 'bot' }]);
        return;
      }
      const ext = filename.includes('.') ? filename.slice(filename.lastIndexOf('.')) : '.xlsx';
      const displayName = action.category ? `${action.category}${ext}` : filename;
      setMessages(prev => [...prev, {
        text: `Your data is ready as <strong>${displayName}</strong>. What would you like to do?`,
        sender: 'bot',
        suggested_actions: [
          { text: `Download ${displayName}`, action: 'do_file_download', filename, displayName },
          { text: 'Enhance with SDV', action: 'enhance_data', filename, category: action.category },
          { text: 'Done', action: 'start_over' },
        ],
      }]);
    },

    do_file_download: () => {
      const { filename, displayName } = action;
      const downloadUrl = `${buildApiUrl('EXPORT_USE_CASE')}/chat-download/${filename}`;
      fetch(downloadUrl)
        .then(res => res.blob())
        .then(blob => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = displayName || filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        })
        .catch(() => window.open(downloadUrl, '_blank'));
      setMessages(prev => [...prev, {
        text: `Downloading <strong>${displayName || filename}</strong>...`,
        sender: 'bot',
        suggested_actions: [{ text: 'Done', action: 'start_over' }],
      }]);
    },

    enhance_confirm_accept: async () => {
      const filename = action.filename;
      try {
        const resp = await fetch(`${buildApiUrl('EXPORT_USE_CASE')}/chat-enhance-confirm`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'accept' }),
        });
        const data = await resp.json();
        const finalFile = data.file || filename;
        const actualExt = finalFile.includes('.') ? finalFile.slice(finalFile.lastIndexOf('.')) : '.xlsx';
        const displayName = action.category ? `${action.category}${actualExt}` : finalFile;
        setMessages(prev => [...prev, {
          text: data.success
            ? `Saved — <strong>${data.total_rows} rows</strong>, ${data.columns_updated?.length ?? 1} column(s) updated.`
            : `Save failed: ${data.detail || data.message}`,
          sender: 'bot',
          ...(data.success && {
            suggested_actions: [
              { text: `Download ${displayName}`, action: 'do_file_download', filename: finalFile, displayName },
              { text: 'Done', action: 'start_over' },
            ],
          }),
        }]);
      } catch (e) {
        setMessages(prev => [...prev, { text: `Error saving: ${e.message}`, sender: 'bot' }]);
      }
    },

    enhance_accept_and_change: async () => {
      const { filename, columns_updated: acceptedCols = [], sheets_preview: sheets = [], available_columns: availableCols = [], table_columns_map: tableColumnsMap = {} } = action;
      try {
        const resp = await fetch(`${buildApiUrl('EXPORT_USE_CASE')}/chat-enhance-confirm`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'accept' }),
        });
        const data = await resp.json();
        if (!data.success) throw new Error(data.detail || data.message || 'Save failed');

        const savedFile = data.file || filename;

        // Build accepted preview HTML with highlights still visible
        const acceptedSet = new Set(acceptedCols);
        const buildSheetHtml = (sheet, isFirst) => {
          const headerCells = sheet.columns.map(c =>
            `<th style="border:1px solid #d1d5db;padding:4px 8px;background:${acceptedSet.has(c) ? '#dcfce7' : '#f3f4f6'};white-space:nowrap;font-size:11px;position:sticky;top:0;">${c}${acceptedSet.has(c) ? ' ✓' : ''}</th>`
          ).join('');
          const bodyRows = sheet.rows.map(row =>
            `<tr>${sheet.columns.map(c => `<td style="border:1px solid #d1d5db;padding:4px 8px;white-space:nowrap;font-size:11px;${acceptedSet.has(c) ? 'background:#f0fdf4;' : ''}">${row[c] ?? ''}</td>`).join('')}</tr>`
          ).join('');
          return `<details${isFirst ? ' open' : ''} style="margin-bottom:6px;border:1px solid #e5e7eb;border-radius:6px;overflow:hidden;">
<summary style="padding:6px 10px;background:#f9fafb;cursor:pointer;font-size:12px;font-weight:600;list-style:none;display:flex;justify-content:space-between;">
  <span>${sheet.name}</span><span style="color:#6b7280;font-weight:400;">${sheet.total_rows} rows · ${sheet.columns.length} cols</span>
</summary>
<div style="overflow-x:auto;overflow-y:auto;max-height:260px;">
  <table style="border-collapse:collapse;width:100%;"><thead><tr>${headerCells}</tr></thead><tbody>${bodyRows}</tbody></table>
</div>
</details>`;
        };

        const sheetsHtml = sheets.map((sh, i) => buildSheetHtml(sh, i === 0)).join('');
        const colList = availableCols.slice(0, 15).join(', ');
        const extra = availableCols.length > 15 ? ` (+${availableCols.length - 15} more)` : '';

        setMessages(prev => [...prev, {
          text: `<p style="font-size:11px;color:#166534;margin:0 0 6px;">✓ Saved — <strong>${acceptedCols.join(', ')}</strong> accepted.</p>${sheetsHtml}<br/>What would you like to change next?<br/><em style="font-size:12px;color:#4b5563;">Available columns: ${colList}${extra}</em>`,
          sender: 'bot',
        }]);

        setPendingEnhance({ pendingAction: 'enhance_do_regenerate', available_columns: availableCols, table_columns_map: tableColumnsMap, file_id: savedFile, category: action.category });
      } catch (e) {
        setMessages(prev => [...prev, { text: `Error saving: ${e.message}`, sender: 'bot' }]);
      }
    },

    enhance_confirm_reject: async () => {
      try {
        await fetch(`${buildApiUrl('EXPORT_USE_CASE')}/chat-enhance-confirm`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'reject' }),
        });
        setMessages(prev => [...prev, { text: 'Changes discarded.', sender: 'bot' }]);
      } catch (e) {
        setMessages(prev => [...prev, { text: `Error: ${e.message}`, sender: 'bot' }]);
      }
    },

    chat_confirm_reject: async () => {
      try {
        await fetch(`${buildApiUrl('EXPORT_USE_CASE')}/chat-confirm`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'reject' }),
        });
        setMessages(prev => [...prev, { text: 'Data discarded.', sender: 'bot' }]);
      } catch (e) {
        setMessages(prev => [...prev, { text: `Error: ${e.message}`, sender: 'bot' }]);
      }
    },

    // ── A3GoldDemo augmentation flow ──────────────────────────────────────

    use_existing_data: () => {
      const tables = action.tables || [];
      if (tables.length === 0) {
        setMessages(prev => [...prev, { text: 'No table data available.', sender: 'bot' }]);
        return;
      }
      const tableButtons = tables.map(t => ({
        text: `${t.name} (${t.rows.length} rows, ${t.columns.length} cols)`,
        action: 'use_existing_table_selected',
        tables,
        primary_table: t.name,
        category: action.category,
      }));
      setMessages(prev => [...prev, {
        text: `Which table from <strong>A3GoldDemo</strong> would you like to use?`,
        sender: 'bot',
        suggested_actions: [
          ...tableButtons,
          { text: 'Cancel', action: 'start_over' },
        ],
      }]);
    },

    use_existing_table_selected: async () => {
      const tables = action.tables || [];
      const primary = tables.find(t => t.name === action.primary_table) || tables[0];
      const database = action.database || 'A3GoldDemo';
      if (!primary) {
        setMessages(prev => [...prev, { text: 'No table data available.', sender: 'bot' }]);
        return;
      }

      setMessages(prev => [...prev, {
        text: `Loading data for <strong>${primary.name}</strong>...`,
        sender: 'bot',
      }]);

      // Helper: build a scrollable HTML data table
      const buildTableHtml = (tbl, isFirst = true) => {
        if (!tbl.columns || tbl.columns.length === 0) return '';
        const headerCells = tbl.columns.map(c =>
          `<th style="border:1px solid #d1d5db;padding:4px 8px;background:#f3f4f6;white-space:nowrap;font-size:11px;position:sticky;top:0;">${c}</th>`
        ).join('');
        const bodyRows = (tbl.rows || []).map(row =>
          `<tr>${tbl.columns.map(c => `<td style="border:1px solid #d1d5db;padding:4px 8px;white-space:nowrap;font-size:11px;">${row[c] ?? ''}</td>`).join('')}</tr>`
        ).join('');
        const totalRows = tbl.total_rows || tbl.rows?.length || 0;
        return `<details${isFirst ? ' open' : ''} style="margin-bottom:6px;border:1px solid #e5e7eb;border-radius:6px;overflow:hidden;">
<summary style="padding:6px 10px;background:#f9fafb;cursor:pointer;font-size:12px;font-weight:600;list-style:none;display:flex;justify-content:space-between;">
  <span>${tbl.name}</span><span style="color:#6b7280;font-weight:400;">${totalRows} rows · ${tbl.columns.length} cols</span>
</summary>
<div style="overflow-x:auto;overflow-y:auto;max-height:240px;">
  <table style="border-collapse:collapse;width:100%;"><thead><tr>${headerCells}</tr></thead><tbody>${bodyRows}</tbody></table>
</div>
</details>`;
      };

      let connectedTableData = [];  // [{name, columns, rows, fkLinks}]

      try {
        // Fetch foreign key relationships
        const fkResp = await fetch(`${buildApiUrl('SYNTHIQ')}/discovery/foreign-keys`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ database, schema: 'dbo', table: primary.name }),
        });
        if (fkResp.ok) {
          const fkData = await fkResp.json();
          if (Array.isArray(fkData) && fkData.length > 0) {
            // Group FK rows by connected table name
            const tableMap = {};
            for (const fk of fkData) {
              const other = fk.source_table === primary.name ? fk.target_table : fk.source_table;
              if (other && other !== primary.name) {
                if (!tableMap[other]) tableMap[other] = [];
                tableMap[other].push(`${fk.source_column} → ${fk.target_column}`);
              }
            }
            // Fetch a sample of each connected table's data via /discovery/table
            await Promise.all(Object.entries(tableMap).map(async ([tblName, links]) => {
              try {
                const dataResp = await fetch(`${buildApiUrl('SYNTHIQ')}/discovery/table`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ database, schema: 'dbo', table: tblName, limit: 10 }),
                });
                if (dataResp.ok) {
                  const d = await dataResp.json();
                  connectedTableData.push({
                    name: tblName,
                    columns: d.rows?.length > 0 ? Object.keys(d.rows[0]) : [],
                    rows: d.rows || [],
                    fkLinks: links,
                    total_rows: d.count,
                  });
                } else {
                  connectedTableData.push({ name: tblName, columns: [], rows: [], fkLinks: links });
                }
              } catch (_) {
                connectedTableData.push({ name: tblName, columns: [], rows: [], fkLinks: links });
              }
            }));
          }
        }
      } catch (_) {}

      // Build HTML
      const primaryHtml = buildTableHtml(primary, true);

      const connectedHtml = connectedTableData.length > 0
        ? `<br/><strong style="font-size:12px;color:#374151;">Connected tables (will also be generated by SDV):</strong><br/>` +
          connectedTableData.map((ct) => {
            const linkLabel = `<span style="font-size:11px;color:#6b7280;margin-left:4px;">${ct.fkLinks.join(', ')}</span>`;
            if (ct.columns.length > 0) {
              return buildTableHtml(ct, false).replace(
                `<span>${ct.name}</span>`,
                `<span>${ct.name}${linkLabel}</span>`
              );
            }
            return `<div style="font-size:12px;padding:4px 0;"><strong>${ct.name}</strong>${linkLabel}</div>`;
          }).join('')
        : '';

      setMessages(prev => {
        const updated = [...prev];
        updated[updated.length - 1] = {
          text: `${primaryHtml}${connectedHtml}<br/>What would you like to do?`,
          sender: 'bot',
          suggested_actions: [
            { text: 'Generate with SDV as-is', action: 'augment_direct_sdv', tables, primary_table: primary.name, database, category: action.category },
            { text: 'Enhance / add data first', action: 'augment_prompt_add', tables, primary_table: primary.name, database, category: action.category },
            { text: 'Cancel', action: 'start_over' },
          ],
        };
        return updated;
      });
    },

    augment_direct_sdv: async () => {
      const tables = action.tables || [];
      const primary = tables.find(t => t.name === action.primary_table) || tables[0];
      const database = action.database || 'A3GoldDemo';
      if (!primary) {
        setMessages(prev => [...prev, { text: 'No table data available.', sender: 'bot' }]);
        return;
      }

      setMessages(prev => [...prev, {
        text: `Running SDV generation on <strong>${primary.name}</strong> from <strong>${database}</strong> — this may take a minute...`,
        sender: 'bot',
      }]);

      try {
        const genResp = await fetch(`${buildApiUrl('EXPORT_USE_CASE')}/generate-from-new-db`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            database,
            schema: 'dbo',
            tables: [primary.name],
            scale: 2,
          }),
        });

        const genData = await genResp.json();
        if (!genData.success) throw new Error(genData.detail || 'SDV generation failed');

        const outputFile = genData.generation?.outputFiles?.[0];
        const sheets = genData.sheets_preview || [];
        const totalRows = sheets.reduce((s, sh) => s + sh.total_rows, 0) || outputFile?.rows || '?';
        const availableCols = sheets.length > 0 ? sheets[0].columns : primary.columns;

        const buildSheetHtml = (sheet, isFirst) => {
          const headerCells = sheet.columns.map(c =>
            `<th style="border:1px solid #d1d5db;padding:4px 8px;background:#f3f4f6;white-space:nowrap;font-size:11px;position:sticky;top:0;">${c}</th>`
          ).join('');
          const bodyRows = sheet.rows.map(row =>
            `<tr>${sheet.columns.map(c => `<td style="border:1px solid #d1d5db;padding:4px 8px;white-space:nowrap;font-size:11px;">${row[c] ?? ''}</td>`).join('')}</tr>`
          ).join('');
          return `<details${isFirst ? ' open' : ''} style="margin-bottom:6px;border:1px solid #e5e7eb;border-radius:6px;overflow:hidden;">
<summary style="padding:6px 10px;background:#f9fafb;cursor:pointer;font-size:12px;font-weight:600;list-style:none;display:flex;justify-content:space-between;">
  <span>${sheet.name}</span><span style="color:#6b7280;font-weight:400;">${sheet.total_rows} rows · ${sheet.columns.length} cols</span>
</summary>
<div style="overflow-x:auto;overflow-y:auto;max-height:260px;">
  <table style="border-collapse:collapse;width:100%;"><thead><tr>${headerCells}</tr></thead><tbody>${bodyRows}</tbody></table>
</div>
</details>`;
        };

        const sheetsHtml = sheets.map((sh, i) => buildSheetHtml(sh, i === 0)).join('');

        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = {
            text: `SDV generation complete — <strong>${totalRows} rows</strong> across <strong>${sheets.length || 1}</strong> sheet(s).<br/>${sheetsHtml}<br/>Accept the data or tell us what to change.`,
            sender: 'bot',
            suggested_actions: [
              { text: 'Accept & download', action: 'enhance_direct_download', filename: outputFile?.downloadId || outputFile?.name, category: action.category },
              { text: 'Change something', action: 'enhance_prompt_regenerate', available_columns: availableCols, table_columns_map: Object.fromEntries(sheets.map(sh => [sh.name, sh.columns])), file_id: outputFile?.downloadId || outputFile?.name, category: action.category },
              { text: 'Cancel', action: 'start_over' },
            ],
          };
          return updated;
        });
      } catch (e) {
        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = { text: `Generation failed: ${e.message}`, sender: 'bot' };
          return updated;
        });
      }
    },

    augment_prompt_add: () => {
      const tables = action.tables || [];
      const primary = tables.find(t => t.name === action.primary_table) || tables[0];
      const cols = primary?.columns || [];
      const colList = cols.slice(0, 15).join(', ');
      const extra = cols.length > 15 ? ` (+${cols.length - 15} more)` : '';
      setMessages(prev => [...prev, {
        text: `What would you like to add or change in <strong>${primary?.name || 'the table'}</strong>? Type your instruction below.<br/><br/><em style="font-size:12px;color:#4b5563;">Columns: ${colList}${extra}</em><br/><span style="font-size:11px;color:#9ca3af;">e.g. "Add 20 rows for customers aged 25–35" or "Set salary between 50000 and 120000"</span>`,
        sender: 'bot',
      }]);
      setPendingEnhance({ pendingAction: 'augment_do_add', tables, primary_table: action.primary_table });
    },

    augment_do_add: async () => {
      const instruction = action.instruction;
      const tables = action.tables || [];
      const primary = tables.find(t => t.name === action.primary_table) || tables[0];
      if (!primary) {
        setMessages(prev => [...prev, { text: 'No table data available.', sender: 'bot' }]);
        return;
      }

      setMessages(prev => [...prev, {
        text: `Generating new rows for <strong>${primary.name}</strong>: "<em>${instruction}</em>"...`,
        sender: 'bot',
      }]);

      try {
        const numRowsMatch = instruction.match(/(?:add|generate|create|insert|make)\s+(\d+)\b/i)
          || instruction.match(/\b(\d+)\s+(?:rows?|records?|entries|items?)\b/i);
        const num_rows = numRowsMatch ? Math.min(Math.max(parseInt(numRowsMatch[1], 10), 1), 10000) : 20;

        const resp = await fetch(`${buildApiUrl('SYNTHIQ')}/chat-augment-rows`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            table_name: primary.name,
            columns: primary.columns,
            sample_rows: primary.rows,
            instruction,
            num_rows,
          }),
        });
        const data = await resp.json();
        if (!data.success) throw new Error(data.detail || 'Row generation failed');

        const cols = data.columns || primary.columns;
        const preview = data.preview || [];
        const headerCells = cols.map(c =>
          `<th style="border:1px solid #d1d5db;padding:4px 8px;background:#fef9c3;white-space:nowrap;font-size:11px;">${c}</th>`
        ).join('');
        const bodyRows = preview.map(row =>
          `<tr>${cols.map(c => `<td style="border:1px solid #d1d5db;padding:4px 8px;white-space:nowrap;font-size:11px;background:#fefce8;">${row[c] ?? ''}</td>`).join('')}</tr>`
        ).join('');
        const previewHtml = preview.length > 0
          ? `<div style="overflow-x:auto;overflow-y:auto;max-height:240px;margin:8px 0;"><table style="border-collapse:collapse;width:100%;"><thead><tr>${headerCells}</tr></thead><tbody>${bodyRows}</tbody></table></div>`
          : '';

        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = {
            text: `Generated <strong>${data.num_new_rows}</strong> new rows for <strong>${primary.name}</strong>. Preview (first 5 shown):<br/>${previewHtml}<br/><span style="font-size:11px;color:#92400e;">✱ Yellow rows are the new additions. Accept to combine with original data and run SDV.</span>`,
            sender: 'bot',
            suggested_actions: [
              {
                text: 'Accept & generate with SDV',
                action: 'augment_accept_and_generate',
                full_rows: primary.rows,
                table_name: primary.name,
                columns: cols,
              },
              { text: 'Try different instruction', action: 'augment_prompt_add', tables, primary_table: primary.name },
              { text: 'Cancel', action: 'augment_reject' },
            ],
          };
          return updated;
        });
      } catch (e) {
        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = { text: `Row generation failed: ${e.message}`, sender: 'bot' };
          return updated;
        });
      }
    },

    augment_accept_and_generate: async () => {
      const full_rows = action.full_rows || [];
      const table_name = action.table_name;
      const columns = action.columns || [];

      setMessages(prev => [...prev, {
        text: `Saving augmented data and running SDV generation — this may take a minute...`,
        sender: 'bot',
      }]);

      try {
        // Step 1: commit augmented rows → xlsx
        const appendResp = await fetch(`${buildApiUrl('SYNTHIQ')}/chat-append-for-sdv`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'accept', full_rows, table_name, columns }),
        });
        const appendData = await appendResp.json();
        if (!appendData.success) throw new Error(appendData.detail || 'Append failed');

        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = {
            text: `Data saved (${appendData.total_rows} rows: ${appendData.original_rows} original + ${appendData.new_rows} new). Running SDV...`,
            sender: 'bot',
          };
          return updated;
        });

        // Step 2: SDV generation using the staged CSV (generate-from-new-db picks it up automatically)
        const stagedTable = appendData.staged_table_name || appendData.table_name;
        const genResp = await fetch(`${buildApiUrl('EXPORT_USE_CASE')}/generate-from-new-db`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            database: 'A3GoldDemo',
            schema: 'dbo',
            tables: [stagedTable],
            scale: 2,
          }),
        });
        const genData = await genResp.json();
        if (!genData.success) throw new Error(genData.detail || 'SDV generation failed');

        const outputFile = genData.generation?.outputFiles?.[0];
        const sheets = genData.sheets_preview || [];
        const totalRows = sheets.reduce((s, sh) => s + sh.total_rows, 0) || outputFile?.rows || '?';
        const availableCols = sheets.length > 0 ? sheets[0].columns : (appendData.columns || []);

        const buildSheetHtml = (sheet, isFirst) => {
          const headerCells = sheet.columns.map(c =>
            `<th style="border:1px solid #d1d5db;padding:4px 8px;background:#f3f4f6;white-space:nowrap;font-size:11px;position:sticky;top:0;">${c}</th>`
          ).join('');
          const bodyRows = sheet.rows.map(row =>
            `<tr>${sheet.columns.map(c => `<td style="border:1px solid #d1d5db;padding:4px 8px;white-space:nowrap;font-size:11px;">${row[c] ?? ''}</td>`).join('')}</tr>`
          ).join('');
          return `<details${isFirst ? ' open' : ''} style="margin-bottom:6px;border:1px solid #e5e7eb;border-radius:6px;overflow:hidden;">
<summary style="padding:6px 10px;background:#f9fafb;cursor:pointer;font-size:12px;font-weight:600;list-style:none;display:flex;justify-content:space-between;">
  <span>${sheet.name}</span><span style="color:#6b7280;font-weight:400;">${sheet.total_rows} rows · ${sheet.columns.length} cols</span>
</summary>
<div style="overflow-x:auto;overflow-y:auto;max-height:260px;">
  <table style="border-collapse:collapse;width:100%;"><thead><tr>${headerCells}</tr></thead><tbody>${bodyRows}</tbody></table>
</div>
</details>`;
        };

        const sheetsHtml = sheets.map((sh, i) => buildSheetHtml(sh, i === 0)).join('');

        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = {
            text: `SDV generation complete — <strong>${totalRows} rows</strong> across <strong>${sheets.length || 1}</strong> sheet(s).<br/>${sheetsHtml}<br/>Accept the data or tell us what to change.`,
            sender: 'bot',
            suggested_actions: [
              { text: 'Accept & download', action: 'enhance_direct_download', filename: outputFile?.downloadId || outputFile?.name, category: action.category },
              { text: 'Change something', action: 'enhance_prompt_regenerate', available_columns: availableCols, table_columns_map: Object.fromEntries(sheets.map(sh => [sh.name, sh.columns])), file_id: outputFile?.downloadId || outputFile?.name, category: action.category },
              { text: 'Cancel', action: 'start_over' },
            ],
          };
          return updated;
        });
      } catch (e) {
        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = { text: `Generation failed: ${e.message}`, sender: 'bot' };
          return updated;
        });
      }
    },

    augment_reject: async () => {
      try {
        await fetch(`${buildApiUrl('SYNTHIQ')}/chat-append-for-sdv`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'reject' }),
        });
      } catch (_) {}
      setMessages(prev => [...prev, { text: 'Augmentation discarded.', sender: 'bot', suggested_actions: [{ text: 'Start over', action: 'start_over' }] }]);
    },

    sdv_from_upload: async () => {
      const { fileId, category, filename } = action;
      setMessages(prev => [...prev, {
        text: `Running SDV generation on <strong>${filename || fileId}</strong> — this may take a minute...`,
        sender: 'bot',
      }]);
      try {
        const resp = await fetch(`${buildApiUrl('EXPORT_USE_CASE')}/sdv-from-file-id`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ fileId }),
        });
        const data = await resp.json();
        if (!data.success) throw new Error(data.detail || 'SDV generation failed');

        const sheets = data.sheets_preview || [];
        const totalRows = sheets.reduce((s, sh) => s + (sh.total_rows || sh.rows?.length || 0), 0);
        const outFile = data.output_file || {};
        const fileRef = outFile.downloadId || outFile.name;
        const availableCols = data.available_columns || [];
        const tableColumnsMap = data.table_columns_map || {};

        const buildSheetHtml = (sheet, isFirst) => {
          const rowCount = sheet.total_rows || sheet.rows?.length || 0;
          const headerCells = sheet.columns.map(c =>
            `<th style="border:1px solid #d1d5db;padding:4px 8px;background:#f3f4f6;white-space:nowrap;font-size:11px;position:sticky;top:0;">${c}</th>`
          ).join('');
          const bodyRows = (sheet.rows || []).slice(0, 10).map(row =>
            `<tr>${sheet.columns.map(c => `<td style="border:1px solid #d1d5db;padding:4px 8px;white-space:nowrap;font-size:11px;">${row[c] ?? ''}</td>`).join('')}</tr>`
          ).join('');
          return `<details${isFirst ? ' open' : ''} style="margin-bottom:6px;border:1px solid #e5e7eb;border-radius:6px;overflow:hidden;">
<summary style="padding:6px 10px;background:#f9fafb;cursor:pointer;font-size:12px;font-weight:600;list-style:none;display:flex;justify-content:space-between;">
  <span>${sheet.name}</span><span style="color:#6b7280;font-weight:400;">${rowCount} rows · ${sheet.columns.length} cols</span>
</summary>
<div style="overflow-x:auto;overflow-y:auto;max-height:260px;">
  <table style="border-collapse:collapse;width:100%;"><thead><tr>${headerCells}</tr></thead><tbody>${bodyRows}</tbody></table>
</div>
</details>`;
        };

        const sheetsHtml = sheets.map((sh, i) => buildSheetHtml(sh, i === 0)).join('');

        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = {
            text: `SDV generation complete — <strong>${totalRows} rows</strong> across <strong>${sheets.length || 1}</strong> sheet(s).<br/>${sheetsHtml}<br/>Accept the data or tell us what to change.`,
            sender: 'bot',
            suggested_actions: [
              { text: 'Accept & download', action: 'enhance_direct_download', filename: fileRef, category },
              { text: 'Change something', action: 'enhance_prompt_regenerate', available_columns: availableCols, table_columns_map: tableColumnsMap, file_id: fileRef, category },
              { text: 'Cancel', action: 'start_over' },
            ],
          };
          return updated;
        });
      } catch (e) {
        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = { text: `SDV generation failed: ${e.message}`, sender: 'bot' };
          return updated;
        });
      }
    },

    upload_for_sdv: () => {
      setMessages(prev => [...prev, {
        text: `Upload your Excel or CSV file using the button below. I'll run SDV generation on it.`,
        sender: 'bot',
      }]);
      setPendingEnhance({ pendingAction: 'sdv_file_uploaded', category: action.category });
    },

    select_different_db: () => {
      setMessages(prev => [...prev, {
        text: `Enter your database connection details below.<br/><em style="font-size:12px;color:#4b5563;">Format: <code>PROVIDER, host, port, database, username, password</code><br/>Example: <code>MSSQL, myserver.database.windows.net, 1433, MyDB, myuser, mypassword</code></em>`,
        sender: 'bot',
      }]);
      setPendingEnhance({ pendingAction: 'connect_db_submit', description: action.description || '' });
    },

    connect_db_submit: async () => {
      const raw = (action.instruction || action.userText || '').trim();
      if (!raw) {
        setMessages(prev => [...prev, { text: 'Please enter connection details in the format shown.', sender: 'bot' }]);
        return;
      }
      const parts = raw.split(',').map(s => s.trim());
      if (parts.length < 6) {
        setMessages(prev => [...prev, {
          text: 'Could not parse connection string. Use: <code>PROVIDER, host, port, database, username, password</code>',
          sender: 'bot',
        }]);
        return;
      }
      const [provider, host, port, database, username, password] = parts;

      setMessages(prev => [...prev, { text: `Testing connection to <strong>${database}</strong> on <strong>${host}</strong>...`, sender: 'bot' }]);

      try {
        const testResp = await fetch(`${buildApiUrl('SYNTHIQ')}/test-connection`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ provider, host, port, database, username, password }),
        });
        const testData = await testResp.json();
        if (!testResp.ok) throw new Error(testData.detail || 'Connection failed');

        // Pick the database to get table list
        const pickResp = await fetch(`${buildApiUrl('SYNTHIQ')}/pick-database`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ database }),
        });
        const pickData = await pickResp.json();
        const tableNames = pickData?.dbo || Object.values(pickData || {})[0] || [];

        if (tableNames.length === 0) {
          setMessages(prev => {
            const updated = [...prev];
            updated[updated.length - 1] = { text: `Connected to <strong>${database}</strong> but no tables found in dbo schema.`, sender: 'bot', suggested_actions: [{ text: 'Try again', action: 'select_different_db', description: action.description }, { text: 'Cancel', action: 'start_over' }] };
            return updated;
          });
          return;
        }

        const tableButtons = tableNames.slice(0, 12).map(t => ({
          text: t,
          action: 'use_existing_table_selected',
          table: t,
          database,
          schema: 'dbo',
        }));

        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = {
            text: `Connected to <strong>${database}</strong> (${testData.currentTime}). Choose a table to generate from:`,
            sender: 'bot',
            suggested_actions: [...tableButtons, { text: 'Cancel', action: 'start_over' }],
          };
          return updated;
        });
      } catch (e) {
        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = {
            text: `Connection failed: ${e.message}`,
            sender: 'bot',
            suggested_actions: [{ text: 'Try again', action: 'select_different_db', description: action.description }, { text: 'Cancel', action: 'start_over' }],
          };
          return updated;
        });
      }
    },

    get_sql: async () => {
      setMessages(prev => [...prev, { text: 'Generating SQL insert statements...', sender: 'bot' }]);
      try {
        const resp = await fetch(`${buildApiUrl('EXPORT_USE_CASE')}/chat-sql?dialect=mssql`);
        const data = await resp.json();
        if (!resp.ok) throw new Error(data.detail || 'SQL generation failed');

        const statements = data.sql || data.statements || data;
        const sqlText = typeof statements === 'string'
          ? statements
          : Array.isArray(statements)
            ? statements.join('\n\n')
            : JSON.stringify(statements, null, 2);

        const copyId = `sql-copy-${Date.now()}`;
        const html = `
          <div style="position:relative;">
            <pre id="${copyId}" style="background:#1e1e1e;color:#d4d4d4;padding:12px;border-radius:6px;font-size:11px;overflow:auto;max-height:320px;white-space:pre-wrap;">${sqlText.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>
            <button onclick="navigator.clipboard.writeText(document.getElementById('${copyId}').innerText).then(()=>this.textContent='Copied!').catch(()=>{})" style="position:absolute;top:6px;right:6px;background:#374151;color:#fff;border:none;border-radius:4px;padding:3px 8px;font-size:11px;cursor:pointer;">Copy</button>
          </div>
          <p style="font-size:12px;color:#6b7280;margin-top:6px;">Run these statements in your database to insert the generated data.</p>`;

        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = {
            text: html,
            sender: 'bot',
            suggested_actions: [
              { text: 'Get PostgreSQL SQL instead', action: 'get_sql_dialect', dialect: 'postgresql', category: action.category },
              { text: 'Done', action: 'start_over' },
            ],
          };
          return updated;
        });
      } catch (e) {
        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = { text: `Failed to generate SQL: ${e.message}`, sender: 'bot' };
          return updated;
        });
      }
    },

    get_sql_dialect: async () => {
      const dialect = action.dialect || 'postgresql';
      setMessages(prev => [...prev, { text: `Generating ${dialect} SQL...`, sender: 'bot' }]);
      try {
        const resp = await fetch(`${buildApiUrl('EXPORT_USE_CASE')}/chat-sql?dialect=${dialect}`);
        const data = await resp.json();
        if (!resp.ok) throw new Error(data.detail || 'SQL generation failed');

        const statements = data.sql || data.statements || data;
        const sqlText = typeof statements === 'string'
          ? statements
          : Array.isArray(statements)
            ? statements.join('\n\n')
            : JSON.stringify(statements, null, 2);

        const copyId = `sql-copy-${Date.now()}`;
        const html = `
          <div style="position:relative;">
            <pre id="${copyId}" style="background:#1e1e1e;color:#d4d4d4;padding:12px;border-radius:6px;font-size:11px;overflow:auto;max-height:320px;white-space:pre-wrap;">${sqlText.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>
            <button onclick="navigator.clipboard.writeText(document.getElementById('${copyId}').innerText).then(()=>this.textContent='Copied!').catch(()=>{})" style="position:absolute;top:6px;right:6px;background:#374151;color:#fff;border:none;border-radius:4px;padding:3px 8px;font-size:11px;cursor:pointer;">Copy</button>
          </div>
          <p style="font-size:12px;color:#6b7280;margin-top:6px;">${dialect.toUpperCase()} insert statements — run these in your database.</p>`;

        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = { text: html, sender: 'bot', suggested_actions: [{ text: 'Done', action: 'start_over' }] };
          return updated;
        });
      } catch (e) {
        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = { text: `Failed: ${e.message}`, sender: 'bot' };
          return updated;
        });
      }
    },

    // ----------------------------------------------------------------
    // Scenario data-generation flow (discover → generate → execute)
    // ----------------------------------------------------------------

    scenario_discover: async () => {
      const description = action.description || '';
      const schema = action.schema || 'dbo';

      setMessages(prev => [...prev, { text: 'Checking your database for relevant tables…', sender: 'bot' }]);

      try {
        const resp = await fetch(
          `${buildApiUrl('EXPORT_USE_CASE')}/scenario-discover?schema=${encodeURIComponent(schema)}`,
          { method: 'POST', headers: { 'Content-Type': 'text/plain' }, body: description }
        );
        const data = await resp.json();
        if (!resp.ok) throw new Error(data.detail || 'Discovery failed');

        const existingRows = (data.existing_tables || []).map(t =>
          `<li style="margin-bottom:6px;">
            <strong>${t.name}</strong>
            <span style="background:#16a34a;color:#fff;font-size:10px;padding:1px 6px;border-radius:9px;margin-left:6px;">INSERT</span>
            <span style="font-size:11px;color:#6b7280;margin-left:6px;">${t.suggested_row_count || ''} rows</span>
            ${t.rationale ? `<br/><span style="font-size:11px;color:#6b7280;">${t.rationale}</span>` : ''}
          </li>`
        ).join('');

        const newRows = (data.new_tables || []).map(t =>
          `<li style="margin-bottom:6px;">
            <strong>${t.name}</strong>
            <span style="background:#7c3aed;color:#fff;font-size:10px;padding:1px 6px;border-radius:9px;margin-left:6px;">CREATE</span>
            <span style="font-size:11px;color:#6b7280;margin-left:6px;">${t.suggested_row_count || ''} rows</span>
            ${t.justification ? `<br/><span style="font-size:11px;color:#6b7280;">${t.justification}</span>` : ''}
          </li>`
        ).join('');

        const planHtml =
          (existingRows ? `<strong>Existing tables (INSERT)</strong><ul style="margin:6px 0 10px 16px;">${existingRows}</ul>` : '') +
          (newRows ? `<strong>New tables (CREATE)</strong><ul style="margin:6px 0 10px 16px;">${newRows}</ul>` : '');

        const total = (data.existing_tables || []).length + (data.new_tables || []).length;

        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = {
            text: `Found <strong>${(data.existing_tables||[]).length}</strong> existing table(s) and <strong>${(data.new_tables||[]).length}</strong> new table(s) for this scenario.<br/><br/>${planHtml}`,
            sender: 'bot',
            suggested_actions: total > 0
              ? [
                  { text: 'Generate rows', action: 'scenario_generate' },
                  { text: 'Cancel', action: 'start_over' },
                ]
              : [{ text: 'Try a different description', action: 'start_over' }],
          };
          return updated;
        });
      } catch (e) {
        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = { text: `Discovery failed: ${e.message}`, sender: 'bot' };
          return updated;
        });
      }
    },

    scenario_generate: async () => {
      setMessages(prev => [...prev, { text: 'Generating synthetic rows that match your existing data patterns…', sender: 'bot' }]);

      try {
        const resp = await fetch(`${buildApiUrl('EXPORT_USE_CASE')}/scenario-generate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
        });
        const data = await resp.json();
        if (!resp.ok) throw new Error(data.detail || 'Generation failed');

        const buildPreview = (t) => {
          const cols = Object.keys(t.preview || {});
          if (!cols.length) return '';
          const rowCount = t.preview[cols[0]]?.length ?? 0;
          const headerCells = cols.map(c => `<th style="border:1px solid #d1d5db;padding:4px 8px;background:#f3f4f6;white-space:nowrap;font-size:11px;">${c}</th>`).join('');
          const bodyRows = Array.from({ length: rowCount }).map((_, i) =>
            `<tr>${cols.map(c => `<td style="border:1px solid #d1d5db;padding:4px 8px;white-space:nowrap;font-size:11px;">${t.preview[c][i] ?? ''}</td>`).join('')}</tr>`
          ).join('');
          return `<div style="overflow-x:auto;margin:4px 0 12px;"><table style="border-collapse:collapse;font-size:11px;"><thead><tr>${headerCells}</tr></thead><tbody>${bodyRows}</tbody></table></div>`;
        };

        const allTables = [...(data.existing_tables || []), ...(data.new_tables || [])];
        const tablesHtml = allTables.map(t =>
          `<div style="margin-bottom:2px;"><strong>${t.name}</strong> — ${t.rows} rows</div>${buildPreview(t)}`
        ).join('');

        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = {
            text: `Preview (first 5 rows per table):<br/><br/>${tablesHtml}How would you like to save this?`,
            sender: 'bot',
            suggested_actions: [
              { text: 'Export to Excel', action: 'scenario_execute', execAction: 'file' },
              { text: 'Write to Database', action: 'scenario_execute', execAction: 'db' },
              { text: 'Cancel', action: 'start_over' },
            ],
          };
          return updated;
        });
      } catch (e) {
        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = { text: `Generation failed: ${e.message}`, sender: 'bot' };
          return updated;
        });
      }
    },

    scenario_execute: async () => {
      const execAction = action.execAction || 'file';
      setMessages(prev => [...prev, {
        text: execAction === 'db' ? 'Writing rows to the database…' : 'Building Excel file…',
        sender: 'bot',
      }]);

      try {
        const resp = await fetch(`${buildApiUrl('EXPORT_USE_CASE')}/scenario-execute`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: execAction }),
        });
        const data = await resp.json();
        if (!resp.ok) throw new Error(data.detail || 'Execute failed');

        let resultText;
        let resultActions;

        if (execAction === 'db') {
          const skipped = data.skipped_count || 0;
          const skipNote = skipped > 0 ? ` (${skipped} row${skipped === 1 ? '' : 's'} skipped — already exist in DB)` : '';
          resultText = `Done! <strong>${data.executed_count}</strong> row${data.executed_count === 1 ? '' : 's'} written to the database${skipNote}.`;
          resultActions = [{ text: 'Done', action: 'start_over' }];
        } else {
          const sheetLines = (data.sheets || []).map(s =>
            `${s.name} — ${s.total_rows ?? s.rows} rows` +
            (s.existing_rows != null ? ` (${s.existing_rows} existing + ${s.new_rows} new)` : '')
          ).join('<br/>');
          resultText = `Excel file ready:<br/><br/>${sheetLines}`;
          resultActions = [
            { text: `Download ${data.file}`, action: 'do_file_download', filename: data.file, displayName: data.file },
            { text: 'Generate more with SDV', action: 'enhance_data', filename: data.file },
            { text: 'Done', action: 'start_over' },
          ];
        }

        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = { text: resultText, sender: 'bot', suggested_actions: resultActions };
          return updated;
        });
      } catch (e) {
        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = { text: `Failed: ${e.message}`, sender: 'bot' };
          return updated;
        });
      }
    },

    default: () => executeWithDelay(action.text)
  };

  return handlers[action.action] || handlers.default;
};
